import React, { FC, useMemo } from 'react'
import { ISmileysConfig, ISmileysCustomColorCSSProperties, ISmileysProps } from './types'

import '@aytm/lib/rfd-ui/styles/swidget/smileys.scss'

export const DEFAULT_SMILEYS_CONFIG: ISmileysConfig = {
  2: [
    { value: 0, icon: 'icon-aytmfd-sad-emoticon', emote: 'sad', label: 'sad smiley' },
    { value: 1, icon: 'icon-aytmfd-happy-emoticon', emote: 'happy', label: 'happy smiley' }
  ],
  3: [
    { value: 0, icon: 'icon-aytmfd-sad-emoticon', emote: 'sad', label: 'sad smiley' },
    { value: 1, icon: 'icon-aytmfd-indifferent-emoticon', emote: 'indifferent', label: 'indifferent smiley' },
    { value: 2, icon: 'icon-aytmfd-happy-emoticon', emote: 'happy', label: 'happy smiley' }
  ],
  5: [
    { value: 0, icon: 'icon-aytmfd-angry-emoticon', emote: 'angry', label: 'angry smiley' },
    { value: 1, icon: 'icon-aytmfd-sad-emoticon', emote: 'sad', label: 'sad smiley' },
    { value: 2, icon: 'icon-aytmfd-indifferent-emoticon', emote: 'indifferent', label: 'indifferent smiley' },
    { value: 3, icon: 'icon-aytmfd-happy-emoticon', emote: 'happy', label: 'happy smiley' },
    { value: 4, icon: 'icon-aytmfd-excited-emoticon', emote: 'excited', label: 'excited smiley' }
  ]
}

const Smileys: FC<ISmileysProps> = ({
  typesAmount = 3,
  smileys = DEFAULT_SMILEYS_CONFIG[`${typesAmount}`],
  selected,
  color,
  activeColor,
  focusColor,
  'aria-label': ariaLabel,
  'aria-required': ariaRequired = true,
  onChange,
  onEnter,
  onLeave
}) => {
  const findNextSmiley = () => {
    const currentSmileyIndex = smileys.findIndex(smiley => smiley.value === selected)
    const nextSmileyIndex = currentSmileyIndex + 1
    return smileys[nextSmileyIndex] || smileys[smileys.length - 1]
  }

  const findPrevSmiley = () => {
    const currentSmileyIndex = smileys.findIndex(smiley => smiley.value === selected)
    return 0 < currentSmileyIndex ? smileys[currentSmileyIndex - 1] : smileys[0]
  }

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = event => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        if (onChange) {
          const nextSmiley = findNextSmiley()
          if (nextSmiley.value != selected) {
            onChange(nextSmiley.value)
          }
        }
        break
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        if (onChange) {
          const prevSmiley = findPrevSmiley()
          if (prevSmiley.value != selected) {
            onChange(prevSmiley.value)
          }
        }
        break
      }
      default: {
        break
      }
    }
  }

  const customColorStyle: ISmileysCustomColorCSSProperties = useMemo(
    () =>
      color || activeColor || focusColor
        ? Object.assign(
            {},
            color ? { '--smileys-color': color } : null,
            activeColor ? { '--smileys-active-color': activeColor } : null,
            focusColor ? { '--smileys-focus-color': focusColor } : null
          )
        : null,
    [activeColor, color, focusColor]
  )

  return (
    <div
      className="rfd-ui-smileys"
      data-testid="SW-Smileys"
      onMouseLeave={onLeave}
      onKeyDown={onKeyDown}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-required={ariaRequired}
      tabIndex={0}
    >
      {smileys.map(smiley => (
        <div
          className={`rfd-ui-smileys-smile ${selected == smiley.value ? 'rfd-ui-smileys-selected-smile' : ''}`}
          style={customColorStyle}
          onClick={onChange ? () => void onChange(smiley.value) : null}
          onMouseEnter={onEnter ? () => void onEnter(smiley.value) : null}
          key={smiley.value}
          data-testid="SW-Smile"
          role="radio"
          aria-checked={selected == smiley.value}
          aria-label={smiley.label || `${smiley.value}`}
        >
          <i
            className={smiley.iconSelected && selected == smiley.value ? smiley.iconSelected : smiley.icon}
            role="presentation"
          />
        </div>
      ))}
    </div>
  )
}
export default Smileys
